import { UserVisibleError } from "@/utils/UserVisibleError"
import { IInfo } from "@/androidPlugins/IInfo"
import * as Sentry from "@sentry/vue"

const appVersion = ref<string>()
const serialNumber = ref<string>()
const deviceSecret = ref<string>()
const merchantId = ref<string>()
const platform = import.meta.env.VITE_PLATFORM
const printingSupported = ref<boolean>()

const hydrationRequired = computed(
  () =>
    appVersion.value === undefined ||
    serialNumber.value === undefined ||
    deviceSecret.value === undefined ||
    merchantId.value === undefined ||
    printingSupported.value === undefined
)

const hydrateDeviceState = async () => {
  try {
    const { value } = await IInfo.getSerialNumber()

    if (value == null) {
      throw new Error()
    }

    serialNumber.value = value
  } catch (error) {
    Sentry.captureException(error)
    throw new UserVisibleError(
      "Error fetching device serial number"
    )
  }

  try {
    const { value } = await IInfo.getAppVersion()
    appVersion.value = value
  } catch (error) {
    Sentry.captureException(error)
    throw new UserVisibleError("Error fetching app version")
  }

  try {
    const { value } = await IInfo.getDeviceSecret()
    deviceSecret.value = value
  } catch (error) {
    Sentry.captureException(error)
    throw new UserVisibleError(
      "Error fetching device secret"
    )
  }

  try {
    const { value } = await IInfo.getMerchantId()
    merchantId.value = value
  } catch (error) {
    if (
      (error as Error).message ===
      "payment-application-not-set-up"
    ) {
      throw new UserVisibleError(
        "Payment application requires setup.\n\nPlease exit takepayments+, open your payment application and follow the steps on screen.\n\nOnce this is complete click 'Restart App' below."
      )
    }

    Sentry.captureException(error)

    throw new UserVisibleError("Error fetching merchant ID")
  }

  try {
    const { value } = await IInfo.getPrintingSupported()
    printingSupported.value = value
  } catch (error) {
    Sentry.captureException(error)

    throw new UserVisibleError(
      "Error fetching device features"
    )
  }
}

export const useDevice = () => ({
  serialNumber: readonly(serialNumber),
  merchantId: readonly(merchantId),
  deviceSecret: readonly(deviceSecret),
  printingSupported: readonly(printingSupported),
  appVersion: readonly(appVersion),
  hydrationRequired,
  hydrateDeviceState,
  platform,
  isCardReceiptsToggleable: platform === "paxPositive",
  isCardholderNotPresentToggleable:
    platform === "ingenicoNerpay",
})
